/* eslint-disable no-console */
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';

import { Card } from '../Card';
import { StarIcon } from '../../Icons';
import { Button } from '../Button';
import { SignIn } from '../SignIn';

import { formatAmount } from '../../helpers/shared';
import { PriceContext } from '../../context/priceContext';

type Props = {
  isOpen: boolean;
  userId?: string;
  email?: string;
  onClose: () => void;
};

const benefits = [
  'Unlimited access to 1,150+ Tools for Procreate',
  'New updates twice a week',
  'Full Access to Academy Lessons',
];

const TrialModal = ({ userId, isOpen, email, onClose }: Props) => {
  const [isAuthOpen, setAuthOpen] = useState(false);

  const { price } = useContext(PriceContext);

  const { trial } = price || {};

  const renderBenefit = (text: string) => (
    <div className='text-main-blue flex items-center mb-2.5 text-base md:text-lg'>
      <StarIcon width={24} height={24} />
      <p className='text-white ml-3 sm:ml-2'>{text}</p>
    </div>
  );

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
  };

  const handleClick = async () => {
    mixpanel.track('Start Free trial', { premium: false, trial: false });

    if (userId && email) {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

      if (stripe) {
        const data = {
          sku: trial.price,
          hasTrial: true,
          userId,
          email,
        };

        const response = await fetch('/.netlify/functions/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }).then((res) => res.json());

        const { error } = await stripe.redirectToCheckout({
          sessionId: response.sessionId,
        });
        console.log(error);
      }
      // const options = {
      //   method: 'POST',
      //   headers: {
      //     Accept: 'application/json',
      //     'Content-Type': 'application/json',
      //     'X-Platform': 'stripe',
      //     Authorization: `Bearer ${process.env.REACT_APP_REVENUE_CAT_KEY}`,
      //   },
      //   body: JSON.stringify({
      //     app_user_id: 'did:ethr:0xe42F4a36018AC95D183607a2Dea7b4B483807A7C',
      //     fetch_token: 'sub_1KiIoOJixBpknj6XwyjHB7GI',
      //   }),
      // };

      // fetch('https://api.revenuecat.com/v1/receipts', options)
      //   .then((response) => response.json())
      //   .then((response) => console.log(response))
      //   .catch((err) => console.error(err));
    } else {
      setAuthOpen(true);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
              relative 
            inline-block 
            overflow-hidden 
            transform 
            transition-all s
            align-middle 
            w-[554px]
            sm:w-full
            `}
            >
              <Card theme='dark' size='medium' onClose={onClose}>
                <div className='relative flex flex-col  max-h-[95vh] md:max-h-fit min-h-[600px]'>
                  <div
                    className={`h-[380px] 
                bg-trial-pattern
                bg-cover
                rounded-t-[22px] flex flex-col justify-end items-start p-8 sm:p-6 md:h-[240px]`}
                  >
                    <h1 className='text-5xl leading-5xl font-bold text-white text-left md:text-4xl md:leading-3xl'>
                      Tools for Procreate Premium
                    </h1>
                  </div>
                  <div className='p-8 text-left sm:p-6'>
                    <ul>
                      {benefits.map((item) => (
                        <li key={item}>{renderBenefit(item)}</li>
                      ))}
                    </ul>
                    <p className='text-4xl leading-4xl text-white font-bold mt-20 sm:mt-12 md:text-3xl md:leading-3xl'>
                      3-days free trial
                    </p>
                    <p className='text-base leading-base text-white mb-8 md:text-sm md:leading-sm'>
                      Then {trial ? formatAmount(trial.amount) : ''}/year. Cancel anytime.
                    </p>
                    <Button theme='blue' size='large' onClick={handleClick}>
                      Start free trial
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
        <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
      </Dialog>
    </Transition.Root>
  );
};

export { TrialModal };
